
import React from 'react';
import { format } from 'date-fns';
import { useIsMobile } from '@/hooks/use-mobile';
import { CalendarIcon, CreditCardIcon, ClockIcon, CheckCircleIcon, XCircleIcon, DollarSignIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { sendTelegramNotification } from '@/services/telegram';
import { formatPaymentReminderNotification } from '@/services/telegram/formatters';
import { useToast } from '@/hooks/use-toast';

interface ProcurementPaymentDetailsProps {
  procurement: any; // Replace with proper type when available
}

export function ProcurementPaymentDetails({ procurement }: ProcurementPaymentDetailsProps) {
  const isMobile = useIsMobile();
  const { toast } = useToast();
  
  const handleSendReminder = async () => {
    try {
      let dueDate: Date;
      let daysRemaining: number;
      
      if (procurement.finance?.payment_term_type === 'upfront') {
        // For upfront payments, use creation date or today if no due date is set
        dueDate = procurement.finance?.payment_due_date 
          ? new Date(procurement.finance.payment_due_date)
          : new Date(procurement.created_at || new Date());
        const today = new Date();
        daysRemaining = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 3600 * 24));
        // If upfront and overdue, set to 0 or negative
        if (daysRemaining < 0) daysRemaining = 0;
      } else {
        dueDate = procurement.finance?.payment_due_date ? new Date(procurement.finance.payment_due_date) : new Date();
        const today = new Date();
        daysRemaining = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 3600 * 24));
      }
      
      // Format text-only payment reminder message
      const message = formatPaymentReminderNotification(procurement, daysRemaining);
      
      // Send as text-only notification (no buttons)
      const result = await sendTelegramNotification(
        message,
        procurement.id
      );
      
      if (result.success) {
        toast({
          title: "Payment reminder sent",
          description: `Notification sent to ${result.sent} recipients.`,
        });
      } else {
        toast({
          title: "Failed to send reminder",
          description: "There was an error sending the notification.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error sending payment reminder:", error);
      toast({
        title: "Error",
        description: "Failed to send payment reminder.",
        variant: "destructive",
      });
    }
  };
  
  const getPaymentStatusColor = () => {
    if (procurement.finance?.payment_confirmed) {
      return 'text-green-600';
    }
    switch (procurement.payment_status) {
      case 'paid':
        return 'text-green-600';
      case 'pending':
        return 'text-amber-600';
      case 'overdue':
        return 'text-red-600';
      default:
        return 'text-muted-foreground';
    }
  };
  
  const getPaymentStatusIcon = () => {
    if (procurement.finance?.payment_confirmed) {
      return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
    }
    switch (procurement.payment_status) {
      case 'paid':
        return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
      case 'overdue':
        return <XCircleIcon className="h-5 w-5 text-red-600" />;
      default:
        return <ClockIcon className="h-5 w-5 text-amber-600" />;
    }
  };

  const getPaymentStatusText = () => {
    if (procurement.finance?.payment_confirmed) {
      return 'Payment Confirmed';
    }
    return procurement.payment_status || 'Not paid';
  };

  const getPaymentDueDateDisplay = () => {
    if (procurement.finance?.payment_term_type === 'upfront') {
      if (procurement.finance?.payment_due_date) {
        return `${new Date(procurement.finance.payment_due_date).toLocaleDateString()} (Due immediately)`;
      }
      return 'Due immediately';
    }
    
    if (procurement.finance?.payment_due_date) {
      return new Date(procurement.finance.payment_due_date).toLocaleDateString();
    }
    
    return 'Not specified';
  };
  
  return (
    <div className="space-y-4">
      <div className={isMobile ? "space-y-4" : "grid grid-cols-1 md:grid-cols-2 gap-4"}>
        <div className="p-4 border rounded-md">
          <h3 className="text-sm font-medium mb-2 flex items-center">
            <CreditCardIcon className="mr-1 h-4 w-4" /> 
            Payment Status
          </h3>
          <div className="flex items-center">
            {getPaymentStatusIcon()}
            <p className={`ml-1 ${getPaymentStatusColor()}`}>
              {getPaymentStatusText()}
            </p>
          </div>
          {procurement.finance?.payment_confirmed_at && (
            <p className="text-xs text-muted-foreground mt-1">
              Confirmed: {format(new Date(procurement.finance.payment_confirmed_at), 'PP')}
            </p>
          )}
        </div>
        
        <div className="p-4 border rounded-md">
          <h3 className="text-sm font-medium mb-2 flex items-center">
            <CalendarIcon className="mr-1 h-4 w-4" />
            Payment Terms
          </h3>
          <p>Type: {procurement.finance?.payment_term_type === 'upfront' ? 'Upfront (Immediate)' : procurement.finance?.payment_term_type || 'Upfront'}</p>
          {procurement.finance?.payment_term_days > 0 && (
            <p>Term: {procurement.finance.payment_term_days} days</p>
          )}
        </div>
      </div>

      {/* Advance Payment Section */}
      {procurement.finance?.has_advance_payment && (
        <div className="border-l-4 border-blue-200 pl-4 space-y-4">
          <h3 className="text-lg font-medium flex items-center">
            <DollarSignIcon className="mr-2 h-5 w-5 text-blue-600" />
            Advance Payment Details
          </h3>
          
          <div className={isMobile ? "space-y-4" : "grid grid-cols-1 md:grid-cols-2 gap-4"}>
            <div className="p-4 border rounded-md bg-blue-50">
              <h4 className="text-sm font-medium mb-2">Advance Amount</h4>
              <p className="text-lg font-semibold text-blue-600">
                Br {procurement.finance?.advance_payment_amount?.toFixed(2) || '0.00'}
              </p>
              <div className="flex items-center mt-1">
                {procurement.finance?.advance_payment_confirmed ? (
                  <>
                    <CheckCircleIcon className="h-4 w-4 text-green-600 mr-1" />
                    <span className="text-xs text-green-600">Confirmed</span>
                  </>
                ) : (
                  <>
                    <ClockIcon className="h-4 w-4 text-amber-600 mr-1" />
                    <span className="text-xs text-amber-600">Pending</span>
                  </>
                )}
              </div>
            </div>
            
            <div className="p-4 border rounded-md">
              <h4 className="text-sm font-medium mb-2">Remaining Amount</h4>
              <p className="text-lg font-semibold">
                Br {procurement.finance?.remaining_payment_amount?.toFixed(2) || '0.00'}
              </p>
              <div className="flex items-center mt-1">
                {procurement.finance?.remaining_payment_confirmed ? (
                  <>
                    <CheckCircleIcon className="h-4 w-4 text-green-600 mr-1" />
                    <span className="text-xs text-green-600">Confirmed</span>
                  </>
                ) : (
                  <>
                    <ClockIcon className="h-4 w-4 text-amber-600 mr-1" />
                    <span className="text-xs text-amber-600">Pending</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Advance Payment Confirmation Details */}
          {procurement.finance?.advance_payment_confirmed && (
            <div className={isMobile ? "space-y-4" : "grid grid-cols-1 md:grid-cols-2 gap-4"}>
              <div className="p-4 border rounded-md bg-green-50">
                <h4 className="text-sm font-medium mb-2">Advance Payment Date</h4>
                <p>{procurement.finance?.advance_payment_date 
                  ? format(new Date(procurement.finance.advance_payment_date), 'PP')
                  : 'Not specified'}</p>
                {procurement.finance?.advance_payment_confirmed_at && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Confirmed: {format(new Date(procurement.finance.advance_payment_confirmed_at), 'PP')}
                  </p>
                )}
              </div>
              
              {procurement.finance?.advance_payment_notes && (
                <div className="p-4 border rounded-md bg-green-50">
                  <h4 className="text-sm font-medium mb-2">Advance Payment Notes</h4>
                  <p className="text-sm">{procurement.finance.advance_payment_notes}</p>
                </div>
              )}
            </div>
          )}

          {/* Remaining Payment Confirmation Details */}
          {procurement.finance?.remaining_payment_confirmed && (
            <div className={isMobile ? "space-y-4" : "grid grid-cols-1 md:grid-cols-2 gap-4"}>
              <div className="p-4 border rounded-md bg-green-50">
                <h4 className="text-sm font-medium mb-2">Remaining Payment Date</h4>
                <p>{procurement.finance?.remaining_payment_date 
                  ? format(new Date(procurement.finance.remaining_payment_date), 'PP')
                  : 'Not specified'}</p>
                {procurement.finance?.remaining_payment_confirmed_at && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Confirmed: {format(new Date(procurement.finance.remaining_payment_confirmed_at), 'PP')}
                  </p>
                )}
              </div>
              
              {procurement.finance?.remaining_payment_notes && (
                <div className="p-4 border rounded-md bg-green-50">
                  <h4 className="text-sm font-medium mb-2">Remaining Payment Notes</h4>
                  <p className="text-sm">{procurement.finance.remaining_payment_notes}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      <div className={isMobile ? "space-y-4" : "grid grid-cols-1 md:grid-cols-2 gap-4"}>
        <div className="p-4 border rounded-md">
          <h3 className="text-sm font-medium mb-2 flex items-center">
            <CalendarIcon className="mr-1 h-4 w-4" />
            Payment Due Date
          </h3>
          <p>{getPaymentDueDateDisplay()}</p>
        </div>
        
        <div className="p-4 border rounded-md">
          <h3 className="text-sm font-medium mb-2">Total Amount</h3>
          <p className="text-lg font-semibold">
            Br {procurement.total_amount?.toFixed(2) || '0.00'}
          </p>
        </div>
      </div>

      {procurement.finance?.payment_confirmed && (
        <div className={isMobile ? "space-y-4" : "grid grid-cols-1 md:grid-cols-2 gap-4"}>
          <div className="p-4 border rounded-md bg-green-50">
            <h3 className="text-sm font-medium mb-2 flex items-center">
              <CheckCircleIcon className="mr-1 h-4 w-4 text-green-600" />
              Actual Payment Date
            </h3>
            <p>{
              procurement.finance?.actual_payment_date 
                ? format(new Date(procurement.finance.actual_payment_date), 'PP')
                : 'Not specified'
            }</p>
          </div>
          
          {procurement.finance?.payment_confirmation_notes && (
            <div className="p-4 border rounded-md bg-green-50">
              <h3 className="text-sm font-medium mb-2">Confirmation Notes</h3>
              <p className="text-sm">{procurement.finance.payment_confirmation_notes}</p>
            </div>
          )}
        </div>
      )}
      
      <div className="flex justify-end space-x-2 mt-4">
        <Button
          variant="outline"
          size={isMobile ? "sm" : "default"}
          onClick={handleSendReminder}
        >
          Send Payment Reminder
        </Button>
      </div>
    </div>
  );
}
